/** KAN-724: вкладка "Не разобрано": письма в ящик организации без привязки к клиенту. */
import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PaginationBar } from '@/components/common/PaginationBar';
import { UnmatchedEmailDialog } from '@/components/emails/UnmatchedEmailDialog';
import { UnmatchedEmailsTable } from '@/components/emails/UnmatchedEmailsTable';
import { useDebounce } from '@/hooks/useDebounce';
import { usePageSize } from '@/hooks/usePageSize';
import { useUnmatchedEmails } from '@/hooks/useUnmatchedEmails';
import type { UnmatchedEmailAssignResult, UnmatchedEmailItem } from '@/types/emailMailbox';

interface Props {
  onAssigned?: (result: UnmatchedEmailAssignResult) => void;
}

export function UnmatchedEmailsPanel({ onAssigned }: Props): JSX.Element {
  const [search, setSearch] = useState('');
  const { page, setPage, pageSize, setPageSize } = usePageSize('emails-unmatched');
  const [opened, setOpened] = useState<UnmatchedEmailItem | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const { data, isLoading, isError } = useUnmatchedEmails({
    search: debouncedSearch || undefined,
    page,
    page_size: pageSize,
  });
  const emails = data?.results ?? [];

  return (
    <div data-testid="unmatched-panel">
      <p className="mb-3 text-sm text-gray-600">
        Письма в ящик, которые не удалось связать с клиентом. Привяжите письмо к клиенту, создайте клиента или
        отметьте, что это не клиент.
      </p>
      <div className="mb-4 relative sm:w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          aria-label="Поиск неразобранных писем"
          data-testid="unmatched-search"
          className="pl-9"
          placeholder="Адрес, имя, тема, текст"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {isLoading && <p className="text-sm text-gray-500">Загрузка...</p>}
      {isError && <p className="text-sm text-red-700">Не удалось загрузить письма</p>}
      {!isLoading && !isError && emails.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="unmatched-empty">
          Неразобранных писем нет
        </p>
      )}
      {emails.length > 0 && (
        <>
          <UnmatchedEmailsTable emails={emails} onOpenEmail={setOpened} />
            <PaginationBar
              page={page}
              pageSize={pageSize}
              count={data?.count ?? 0}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
        </>
      )}
      <UnmatchedEmailDialog email={opened} onClose={() => setOpened(null)} onAssigned={onAssigned} />
    </div>
  );
}
